import { Checkbox } from "@/components/Checkbox/Checkbox";
import { DialogBox } from "@/components/DialogBox/DialogBox";
import { navigateToPrivacyPolicy, navigateToTermsOfService } from "@/components/TermsAndPolicyDialog/utils";
import { useAcceptUserConsentV1Mutation } from "@/store/clientApi";
import { ensureError, localizeError } from "@/utils/helpers";
import { trackConsentChoice } from "@/analytics/tracking";
import { useAuth0 } from "@auth0/auth0-react";
import { Box, Link, Typography } from "@mui/material";
import { useState } from "react";
import toast from "react-hot-toast";
import { Trans, useTranslation } from "react-i18next";

interface TermsAndPolicyDialogProps {
  open: boolean;
  onAccepted: () => void;
}

export const TermsAndPolicyDialog = ({ open, onAccepted }: TermsAndPolicyDialogProps) => {
  const { t } = useTranslation();
  const { logout } = useAuth0();
  const [acceptUserConsent] = useAcceptUserConsentV1Mutation();
  const [acceptedTerms, setAcceptedTerms] = useState<boolean>(false);
  const [acceptedPrivacy, setAcceptedPrivacy] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleAcceptTerms = async () => {
    setIsSubmitting(true);
    try {
      await Promise.all([
        acceptUserConsent({ documentType: "terms" }).unwrap(),
        acceptUserConsent({ documentType: "privacy-policy" }).unwrap()
      ]);
      trackConsentChoice(true);
      onAccepted();
    } catch (err) {
      const error = ensureError(err);
      toast.error(localizeError(t, error));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <DialogBox
      title={t("common:title:termsAndPolicy")}
      sx={{ ".MuiPaper-root": { width: "100%", maxWidth: "800px", margin: "16px" } }}
      message={
        <Box sx={{ display: "flex", flexDirection: "column", gap: "16px" }}>
          <Typography>
            <Trans
              t={t}
              components={{
                terms: <Link component={"button"} onClick={navigateToTermsOfService} />,
                privacy: <Link component={"button"} onClick={navigateToPrivacyPolicy} />
              }}
              i18nKey={"message.reviewAndAgreeToPolicies"}
            />
          </Typography>
          <Box sx={{ display: "flex", justifyContent: "flex-start", alignItems: "center" }}>
            <Checkbox value={acceptedTerms} onChange={(_, checked) => setAcceptedTerms(checked)} />
            <Typography>
              <Trans
                components={{
                  a: <Link component={"button"} onClick={navigateToTermsOfService} />
                }}
                t={t}
                i18nKey={"message.acceptTermsOfService"}
              />
            </Typography>
          </Box>
          <Box sx={{ display: "flex", justifyContent: "flex-start", alignItems: "center" }}>
            <Checkbox value={acceptedPrivacy} onChange={(_, checked) => setAcceptedPrivacy(checked)} />
            <Typography>
              <Trans
                components={{
                  a: <Link component={"button"} onClick={navigateToPrivacyPolicy} />
                }}
                t={t}
                i18nKey={"message.acceptPrivacyPolicy"}
              />
            </Typography>
          </Box>
        </Box>
      }
      onClose={async (approve) => {
        if (!approve || !acceptedPrivacy || !acceptedTerms) {
          trackConsentChoice(false);
          await logout({
            logoutParams: { returnTo: window.location.origin }
          });
          return;
        }

        handleAcceptTerms();
      }}
      open={open}
      setOpen={() => {
        return;
      }}
      args={undefined}
      hideDecline
      hideClose
      confirmText={t("common:button.accept")}
      confirmDisable={isSubmitting || !acceptedPrivacy || !acceptedTerms}
    />
  );
};
